import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { MembershipStartTodayResult, startMembershipToday } from '../../api/memberShip';
import { getItemWithExpiration } from '../../lib/utils';
import { useScheduleConfirmDialog } from '../CreateSchedule/scheduleConfirmDialog';
import { findStartTodayMembership, membershipMonthDay, StartTodayMembership } from './membershipStartToday';

const UNKNOWN_RESULT = '처리 결과를 확인하지 못했어요. 목록을 다시 불러올게요.';
const CONFLICT_CODES = new Set([
    'PAYMENT_DATE_REQUIRED',
    'MEMBERSHIP_TRANSFERRED',
    'SHARED_MEMBERSHIP_NOT_SUPPORTED',
    'MEMBERSHIP_DATES_CHANGED',
    'MEMBERSHIP_NOT_ACTIVE',
    'MEMBERSHIP_SALES_REFUND_OPERATION_IN_PROGRESS',
    // 서버 이름 그대로(membershipSalesRefundProtection.service.ts). 같은 레포 changeCoachError.ts 도 이 이름.
    'MEMBERSHIP_SALES_REFUND_REVIEW_REQUIRED',
    'MEMBERSHIP_USAGE_BUSY',
]);
// 서버의 code는 HTTP 숫자일 수 있다. 도메인 사유는 errorCode를 우선한다.
const getErrorCode = (data: MembershipStartTodayResult['data']): string =>
    data?.errorCode || (typeof data?.code === 'string' ? data.code : '');

type Props = {
    memberId: string;
    coachId: string;
    memberships: readonly StartTodayMembership[];
};

const MembershipStartTodayBanner = ({ memberId, coachId, memberships }: Props) => {
    const queryClient = useQueryClient();
    const location = useLocation();
    const [authorization] = useState(() => getItemWithExpiration('authorization'));
    const [isPending, setIsPending] = useState(false);
    const [notice, setNotice] = useState<{ text: string; success?: boolean } | null>(null);
    const [isDenied, setIsDenied] = useState(false);
    const [hiddenIds, setHiddenIds] = useState<string[]>([]);
    const sessionRef = useRef({ valid: false });
    const requestLock = useRef(false);
    const cancelRetryRef = useRef<(() => void) | null>(null);
    const { openConfirm, closeConfirm, confirmDialog } = useScheduleConfirmDialog();

    useEffect(() => {
        const session = { valid: true };
        sessionRef.current = session;
        requestLock.current = false;
        setIsPending(false);
        setNotice(null);
        setIsDenied(false);
        setHiddenIds([]);
        closeConfirm();
        return () => {
            session.valid = false;
            cancelRetryRef.current?.();
            cancelRetryRef.current = null;
        };
    }, [memberId, coachId, location.key, closeConfirm]);

    const candidate = findStartTodayMembership(memberships, memberId, coachId, new Date());
    const visibleCandidate = !isDenied && candidate && !hiddenIds.includes(candidate._id) ? candidate : null;

    const openStartConfirm = () => {
        if (!visibleCandidate || requestLock.current) return;
        // 창을 열 때 본 값으로 고정한다. 재조회·자정 경계·자동 재시도에도 기대값을 바꾸지 않는다.
        const membership = visibleCandidate;
        const session = sessionRef.current;
        const isCurrent = () => session.valid && sessionRef.current === session;
        const params = {
            membershipId: membership._id,
            expectedActivationDate: membership.activationDate,
            expectedExpirationDate: membership.expirationDate,
            accessToken: authorization,
        };
        // 두 목록 키 관용구(강사·회원 키 / 기존 회원·인증 키)를 모두 지원한다.
        const listFilter = {
            predicate: (query: { queryKey: readonly unknown[] }) => {
                const key = query.queryKey;
                return key[0] === 'getAllMembershipForMemberQuery' &&
                    ((key[1] === coachId && key[2] === memberId) || key[1] === memberId);
            },
        };

        const send = async () => {
            if (!isCurrent() || requestLock.current) return;
            const cache = queryClient.getQueryCache();
            const originalQueries = cache.findAll({ predicate: (query) => listFilter.predicate(query) ||
                (query.queryKey[0] === 'getMembershipForMemberQuery' && query.queryKey[1] === membership._id),
            });
            requestLock.current = true;
            setIsPending(true);
            setNotice(null);
            try {
                let result = await startMembershipToday(params);
                if (!isCurrent()) return;
                const firstCode = getErrorCode(result.data);
                if (result.status === 409 && firstCode === 'MEMBERSHIP_USAGE_BUSY') {
                    setNotice({ text: result.data?.message || '잠시 후 다시 시도해 주세요.' });
                    await new Promise<void>((resolve) => {
                        const timer = window.setTimeout(() => {
                            cancelRetryRef.current = null;
                            resolve();
                        }, 1500);
                        cancelRetryRef.current = () => {
                            window.clearTimeout(timer);
                            resolve();
                        };
                    });
                    if (!isCurrent()) return;
                    result = await startMembershipToday(params);
                    if (!isCurrent()) return;
                }

                const { status, data } = result;
                const code = getErrorCode(data);
                const newStart = data?.membership?.activationDate;
                if (status === 200 && data?.changed === true && newStart && membershipMonthDay(newStart)) {
                    setNotice({ text: `${membershipMonthDay(newStart)}부터 쓸 수 있게 바꿨어요`, success: true });
                } else if (status === 200 && data?.changed === false && data.reason === 'ALREADY_STARTED') {
                    setNotice({ text: '이미 시작된 수강권이에요' });
                } else if (status === 404) {
                    // 404 는 권한 거절이 아니라 "지금 이 수강권을 찾지 못함"이다. 권한 탓으로 안내하지 않는다.
                    setNotice({ text: '이 수강권은 지금 바꿀 수 없어요.' });
                    setIsDenied(true);
                } else if (status === 412 || status === 403) {
                    setNotice({ text: '수강권 시작일을 바꿀 권한이 없어요.' });
                    setIsDenied(true);
                } else if (status === 409 && CONFLICT_CODES.has(code)) {
                    setNotice({ text: data?.message || '수강권 상태가 바뀌었어요. 목록을 다시 불러올게요.' });
                    if (code === 'SHARED_MEMBERSHIP_NOT_SUPPORTED') {
                        setHiddenIds((ids) => [...ids, membership._id]);
                    }
                } else {
                    setNotice({ text: UNKNOWN_RESULT });
                }

                await Promise.all([
                    queryClient.invalidateQueries(listFilter, { throwOnError: true }),
                    queryClient.invalidateQueries({
                        queryKey: ['getMembershipForMemberQuery', membership._id],
                        refetchType: 'none',
                    }),
                ]);
                if (!isCurrent()) return;
                const lists = queryClient.getQueryCache().findAll({ ...listFilter, type: 'active' });
                if (lists.some((query) => {
                    const response = query.state.data as any;
                    return query.state.status === 'error' || response?.status !== 200 ||
                        !Array.isArray(response?.data?.membersMemberships);
                })) setNotice({ text: UNKNOWN_RESULT });
            } catch {
                if (isCurrent()) setNotice({ text: UNKNOWN_RESULT });
            } finally {
                if (isCurrent()) {
                    requestLock.current = false;
                    setIsPending(false);
                } else {
                    // 떠난 화면에는 응답을 적용하지 않는다. 재방문 때 원본 캐시만 다시 읽게 한다.
                    // 로그아웃으로 캐시가 교체됐으면 새 계정의 쿼리는 건드리지 않는다.
                    originalQueries.forEach((query) => {
                        if (cache.get(query.queryHash) === query) query.invalidate();
                    });
                }
            }
        };

        openConfirm({
            title: '수강권 시작일 바꾸기',
            content: `「${membership.name}」 시작일을 오늘(${membershipMonthDay(new Date())})로 바꿔요. 만료일(${membershipMonthDay(membership.expirationDate)})은 그대로예요.`,
            confirmText: '바꾸기',
            cancelText: '취소',
            icon: 'none',
        }, () => { void send(); });
    };

    if (!authorization || (!visibleCandidate && !notice)) return null;

    return (
        <div className="w-full mb-[16px] font-noto-regular text-[14px]" aria-busy={isPending}>
            {visibleCandidate && (
                <div className="flex items-center justify-between gap-[16px] rounded-[8px] border border-BLUE_100 bg-BLUE_50 px-[20px] py-[16px]">
                    <p className="text-LAVEL_2 leading-[22px]">
                        이전 수강권이 종료됐어요. 「{visibleCandidate.name}」을 오늘부터 쓸까요? (지금 시작일 {membershipMonthDay(visibleCandidate.activationDate)})
                    </p>
                    <button
                        type="button"
                        disabled={isPending}
                        onClick={openStartConfirm}
                        className="shrink-0 rounded-[8px] bg-BLUE_500 px-[16px] py-[10px] font-noto-bold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isPending ? '바꾸는 중이에요' : '오늘부터 쓰기'}
                    </button>
                </div>
            )}
            {notice && (
                <p
                    role="status"
                    className={notice.success
                        ? 'mt-[12px] flex items-center gap-[12px] rounded-[8px] border border-solid border-[#D1FAE5] bg-[#ECFDF5] px-[16px] py-[12px] font-noto-medium text-[#065F46] leading-[22px]'
                        : 'mt-[12px] text-LAVEL_2 leading-[22px]'}
                >
                    {notice.success && <CheckCircle2 size={18} color="#047857" className="shrink-0" aria-hidden="true" />}
                    {notice.text}
                </p>
            )}
            {confirmDialog}
        </div>
    );
};

export default MembershipStartTodayBanner;
